import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Stack,
  Avatar,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FormatQuote } from '@mui/icons-material';

const TestimonialsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const sectionBackground = isDark ? theme.palette.background.default : '#f5f8ff';
  const subText = theme.palette.text.secondary;
  const accent = isDark ? theme.palette.primary.light : '#2563eb';
  const cardBackground = theme.palette.background.paper;
  const cardBorder = `1px solid ${alpha(theme.palette.divider, isDark ? 0.45 : 0.12)}`;
  const quoteColor = isDark ? theme.palette.primary.light : '#2563eb';
  const avatarSurface = alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12);
  const avatarColor = isDark ? theme.palette.primary.light : '#0c1b2b';

  const testimonials = [
    {
      name: 'John Mitchell',
      company: 'Mitchell Logistics',
      role: 'Fleet Manager',
      image: 'JM',
      text: 'Carrier Finder Pro has transformed how we manage our freight business. Our search time dropped to minutes and our outreach is fully personalized.',
    },
    {
      name: 'Sarah Rodriguez',
      company: 'Rodriguez Transport',
      role: 'Operations Manager',
      image: 'SR',
      text: 'The workflow is premium and focused. We can verify carriers quickly and keep our team accountable with activity logs.',
    },
    {
      name: 'David Chen',
      company: 'Chen Freight Services',
      role: 'Owner',
      image: 'DC',
      text: 'Analytics and templates are exactly what we needed. It feels like an executive dashboard rather than a basic tool.',
    },
    {
      name: 'Maria Garcia',
      company: 'Garcia & Associates',
      role: 'Logistics Coordinator',
      image: 'MG',
      text: 'The UI is clean and the data is reliable. We replaced three tools with this one platform.',
    },
  ];

  return (
    <Box sx={{ py: { xs: 8, md: 12 }, backgroundColor: sectionBackground }}>
      <Container maxWidth="lg">
        <Stack spacing={2} sx={{ mb: 6, textAlign: 'center' }}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: 2, fontWeight: 700, color: subText }}
          >
            CUSTOMER STORIES
          </Typography>
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{
              fontFamily: '"Sora", "Manrope", sans-serif',
              fontWeight: 800,
            }}
          >
            Trusted by modern logistics teams.
          </Typography>
          <Typography sx={{ color: subText, maxWidth: 680, mx: 'auto', lineHeight: 1.8 }}>
            From boutique carriers to enterprise brokers, teams rely on Carrier Finder Pro to keep
            every outreach compliant and every search fast.
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {testimonials.map((testimonial) => (
            <Grid item xs={12} md={6} key={testimonial.name}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  border: cardBorder,
                  background: cardBackground,
                  boxShadow: isDark ? '0 18px 36px rgba(7,12,23,0.45)' : '0 12px 24px rgba(15,23,42,0.08)',
                }}
              >
                <CardContent sx={{ p: 3.5 }}>
                  <FormatQuote sx={{ fontSize: 34, color: quoteColor, mb: 1 }} />
                  <Typography sx={{ color: subText, lineHeight: 1.8, mb: 3 }}>
                    “{testimonial.text}”
                  </Typography>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: avatarSurface,
                        color: avatarColor,
                        fontWeight: 700,
                      }}
                    >
                      {testimonial.image}
                    </Avatar>
                    <Box>
                      <Typography sx={{ fontWeight: 700 }}>{testimonial.name}</Typography>
                      <Typography variant="caption" sx={{ color: subText }}>
                        {testimonial.role} · {testimonial.company}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;
